import { useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  FileImage,
  Printer,
  Star,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { estiloEvento, estiloStatusTurno, nomeMes, periodoDoTurno } from "@/lib/utils";
import { ModalTurnosDoDia } from "./ModalTurnosDoDia";
import { ModalCopiarPara } from "./ModalCopiarPara";
import type { EventoEspecial, TurnoCalendario } from "@/types";
import { usePermissions } from "@/hooks/usePermissions";
import { ROTULO_TIPO_PLURAL, type FiltroTipo } from "@/lib/tipoProfissional";

function diasNoMes(ano: number, mes: number) {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

export function CalendarioMensal() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getUTCFullYear());
  const [mes, setMes] = useState(hoje.getUTCMonth() + 1);
  const [filtro, setFiltro] = useState<FiltroTipo>("todos");
  const [diaAberto, setDiaAberto] = useState<string | null>(null);
  const [copiar, setCopiar] = useState<{ modo: "dia" | "semana"; data: string } | null>(null);
  const [exportando, setExportando] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // Admin OU coordenação de escala (24/09) copiam e excluem dia/semana.
  const { podeGerirEscala } = usePermissions();
  const utils = trpc.useUtils();

  const turnosQ = trpc.turnos.listarPorMes.useQuery({
    ano,
    mes,
    tipoProfissional: filtro === "todos" ? undefined : filtro,
  });
  const tiposTurnoQ = trpc.tiposTurno.listar.useQuery();
  const eventosQ = trpc.eventosEspeciais.listarPorMes.useQuery({ ano, mes });
  const excluirDia = trpc.turnos.excluirDia.useMutation({
    onSuccess: (r) => {
      alert(`${r.excluidos} turnos excluídos.`);
      utils.turnos.listarPorMes.invalidate();
    },
  });

  const grade = useMemo(() => {
    const primeiro = new Date(Date.UTC(ano, mes - 1, 1));
    const offset = primeiro.getUTCDay();
    const total = diasNoMes(ano, mes);
    const cells: Array<{ iso: string | null; dia: number | null }> = [];
    for (let i = 0; i < offset; i++) cells.push({ iso: null, dia: null });
    for (let d = 1; d <= total; d++) {
      const iso = `${ano}-${String(mes).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ iso, dia: d });
    }
    while (cells.length % 7 !== 0) cells.push({ iso: null, dia: null });
    return cells;
  }, [ano, mes]);

  const eventosPorData = useMemo(() => {
    const map = new Map<string, EventoEspecial[]>();
    (eventosQ.data ?? []).forEach((e) => {
      const arr = map.get(e.data) ?? [];
      arr.push(e);
      map.set(e.data, arr);
    });
    return map;
  }, [eventosQ.data]);

  const turnosPorData = useMemo(() => {
    const map = new Map<string, TurnoCalendario[]>();
    (turnosQ.data ?? []).forEach((t) => {
      const arr = map.get(t.data) ?? [];
      arr.push(t);
      map.set(t.data, arr);
    });
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const ai = a.horaInicio === "00:00" ? "24:00" : a.horaInicio;
        const bi = b.horaInicio === "00:00" ? "24:00" : b.horaInicio;
        if (ai !== bi) return ai.localeCompare(bi);
        return a.profissionalNome.localeCompare(b.profissionalNome);
      });
    }
    return map;
  }, [turnosQ.data]);

  function navegar(delta: number) {
    let novo = mes + delta;
    let novoAno = ano;
    if (novo < 1) { novo = 12; novoAno--; }
    if (novo > 12) { novo = 1; novoAno++; }
    setMes(novo);
    setAno(novoAno);
  }

  function refresh() {
    turnosQ.refetch();
  }

  function imprimir() {
    window.print();
  }

  async function baixarPng() {
    if (!printRef.current) return;
    setExportando(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(printRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement("a");
      link.download = `escalas-${nomeMes(mes).toLowerCase()}-${ano}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      console.error(e);
      alert("Falha ao gerar imagem.");
    } finally {
      setExportando(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between no-print">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navegar(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-44 text-center text-lg font-semibold text-slate-900">
            {nomeMes(mes)} {ano}
          </div>
          <Button variant="outline" size="icon" onClick={() => navegar(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filtro} onValueChange={(v) => setFiltro(v as FiltroTipo)}>
            <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="veterinario">{ROTULO_TIPO_PLURAL.veterinario}</SelectItem>
              <SelectItem value="auxiliar">{ROTULO_TIPO_PLURAL.auxiliar}</SelectItem>
              <SelectItem value="apoio">{ROTULO_TIPO_PLURAL.apoio}</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exportando}>
                <Download className="mr-2 h-4 w-4" />
                {exportando ? "Gerando..." : "Exportar"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={imprimir}>
                <Printer className="mr-2 h-4 w-4" />
                Imprimir / Salvar PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={baixarPng}>
                <FileImage className="mr-2 h-4 w-4" />
                Baixar imagem (PNG)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div ref={printRef} className="print-area space-y-3">
        <div className="hidden print:block">
          <h1 className="text-xl font-bold text-slate-900">
            Vet Center Miguel — Calendário de Turnos
          </h1>
          <p className="text-sm text-slate-600">
            {nomeMes(mes)} {ano}
            {filtro !== "todos" && ` · Filtro: ${ROTULO_TIPO_PLURAL[filtro]}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600">
          <span className="font-semibold uppercase tracking-wide text-slate-500">Períodos:</span>
          <LegendaPeriodo sigla="M" cor="bg-amber-100 text-amber-800 ring-amber-200" desc="Manhã (06–12h)" />
          <LegendaPeriodo sigla="T" cor="bg-sky-100 text-sky-800 ring-sky-200" desc="Tarde (12–18h)" />
          <LegendaPeriodo sigla="N" cor="bg-indigo-100 text-indigo-800 ring-indigo-200" desc="Noite (18–24h)" />
          <LegendaPeriodo sigla="P" cor="bg-slate-200 text-slate-700 ring-slate-300" desc="Pernoite (00–06h)" />
          <span className="text-slate-400">Combinações: M+T, T+N, N+P</span>
          <span className="text-slate-400">
            Conferência: R realizado · C cancelado · F falta · A sobreaviso acionado
          </span>
        </div>

        <EventosDoMes eventos={eventosQ.data ?? []} />

        <ObservacoesPlantao />

      <Card>
        <CardContent className="p-3">
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
            {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
              <div key={d} className="py-2">{d}</div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {grade.map((cell, i) => {
              if (!cell.iso) return <div key={i} className="min-h-32 rounded-md bg-slate-50/50" />;
              const turnos = turnosPorData.get(cell.iso) ?? [];
              const eventos = eventosPorData.get(cell.iso) ?? [];
              return (
                <DiaCelula
                  key={cell.iso}
                  iso={cell.iso}
                  dia={cell.dia!}
                  turnos={turnos}
                  eventos={eventos}
                  podeGerir={podeGerirEscala}
                  onAbrir={() => setDiaAberto(cell.iso)}
                  onCopiarDia={() => setCopiar({ modo: "dia", data: cell.iso! })}
                  onCopiarSemana={() => setCopiar({ modo: "semana", data: cell.iso! })}
                  onExcluir={() => {
                    if (turnos.length === 0) {
                      alert("Sem turnos visíveis para excluir neste dia (com o filtro atual).");
                      return;
                    }
                    if (
                      confirm(
                        `Excluir ${turnos.length} turno(s) do dia ${cell.iso} (filtro: ${filtro})?`,
                      )
                    ) {
                      excluirDia.mutate({ data: cell.iso!, tipoFiltro: filtro });
                    }
                  }}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>

        <Card>
          <CardContent className="p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Tipos de turno
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {(tiposTurnoQ.data ?? []).map((tt) => (
                <div key={tt.id} className="flex items-center gap-2 text-xs text-slate-700">
                  <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: tt.cor }} />
                  <span className="truncate" title={tt.nome}>{tt.nome}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {diaAberto && (
        <ModalTurnosDoDia
          dataIso={diaAberto}
          turnos={turnosPorData.get(diaAberto) ?? []}
          open
          onOpenChange={(o) => !o && setDiaAberto(null)}
          onChanged={refresh}
        />
      )}
      {copiar && (
        <ModalCopiarPara
          modo={copiar.modo}
          dataOrigem={copiar.data}
          tipoFiltro={filtro}
          open
          onOpenChange={(o) => !o && setCopiar(null)}
          onCopiado={() => {
            setCopiar(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function ObservacoesPlantao() {
  const q = trpc.configuracoes.obterRegrasPlantao.useQuery();
  const secoes = q.data?.secoes ?? [];

  if (q.isLoading || secoes.length === 0) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Boas práticas de prestação de serviço
          </span>
          <span className="text-[10px] text-slate-400">
            (orientações gerais — cada profissional autônomo/PJ tem autonomia sobre sua agenda)
          </span>
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2">
          {secoes.map((s) => (
            <section key={s.titulo}>
              <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-brand-700">
                {s.titulo}
              </div>
              <ul className="space-y-1.5 text-sm text-slate-700">
                {s.regras.map((r, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function EventosDoMes({ eventos }: { eventos: EventoEspecial[] }) {
  if (eventos.length === 0) return null;
  const ordenados = [...eventos].sort((a, b) => a.data.localeCompare(b.data));
  return (
    <Card>
      <CardContent className="p-3">
        <div className="mb-2 flex items-center gap-2">
          <Star className="h-3.5 w-3.5 text-rose-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Feriados, avisos e aniversários do mês
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {ordenados.map((e) => {
            const est = estiloEvento(e.tipo);
            const [, m, d] = e.data.split("-");
            return (
              <span
                key={e.id}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${est.pillClasse}`}
                title={e.descricao ?? est.label}
              >
                <span className="font-bold">{d}/{m}</span>
                <span>{e.nome}</span>
              </span>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function LegendaPeriodo({ sigla, cor, desc }: { sigla: string; cor: string; desc: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ring-1 ring-inset ${cor}`}>
        {sigla}
      </span>
      <span>{desc}</span>
    </span>
  );
}

function DiaCelula({
  iso,
  dia,
  turnos,
  eventos,
  podeGerir,
  onAbrir,
  onCopiarDia,
  onCopiarSemana,
  onExcluir,
}: {
  iso: string;
  dia: number;
  turnos: TurnoCalendario[];
  eventos: EventoEspecial[];
  podeGerir: boolean;
  onAbrir: () => void;
  onCopiarDia: () => void;
  onCopiarSemana: () => void;
  onExcluir: () => void;
}) {
  const eventoPrincipal = eventos[0];
  const estilo = eventoPrincipal ? estiloEvento(eventoPrincipal.tipo) : null;
  const tooltipEventos = eventos
    .map((e) => `${e.nome}${e.descricao ? ` — ${e.descricao}` : ""}`)
    .join("\n");
  return (
    <div
      className={`calendario-celula group relative flex min-h-32 flex-col rounded-md border border-slate-200 bg-white p-1 transition hover:border-brand-300 ${
        estilo ? estilo.celulaClasse : ""
      }`}
    >
      <div className="flex items-center justify-between px-1">
        <button
          onClick={onAbrir}
          className={`flex items-center gap-1 text-xs font-semibold hover:text-brand-700 ${
            estilo ? estilo.numeroClasse : "text-slate-700"
          }`}
          title={tooltipEventos || "Ver / adicionar turnos"}
        >
          {estilo && <Star className="h-3 w-3 fill-current" />}
          {dia}
        </button>
        {podeGerir && (
          <div className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
            <IconBtn icon={Copy} title="Copiar dia inteiro (filtrado)" onClick={onCopiarDia} />
            <IconBtn icon={CalendarDays} title="Copiar semana inteira (filtrada)" onClick={onCopiarSemana} />
            <IconBtn icon={Trash2} title="Excluir turnos do dia (filtrado)" onClick={onExcluir} variant="danger" />
          </div>
        )}
      </div>
      {eventoPrincipal && (
        <div
          className={`mx-1 mt-1 truncate rounded px-1 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${estilo!.pillClasse}`}
          title={tooltipEventos}
        >
          {eventoPrincipal.nome}
          {eventos.length > 1 && ` +${eventos.length - 1}`}
        </div>
      )}
      <button
        onClick={onAbrir}
        className="mt-1 flex flex-1 cursor-pointer flex-col gap-0.5 text-left"
      >
        {turnos.map((t) => {
          const per = periodoDoTurno(t.horaInicio, t.horaFim);
          // F2: turno ja conferido ganha a marca do que aconteceu (R/C/F).
          // Turno sem conferencia nao ganha nada — a ausencia e o recado.
          const est = estiloStatusTurno(t.status);
          const risco = t.status === "cancelado" || t.status === "falta";
          return (
            <span
              key={t.id}
              className={`flex items-center gap-1 truncate rounded px-1 py-0.5 text-[11px] font-medium text-white ${
                risco ? "opacity-60" : ""
              }`}
              style={{ backgroundColor: t.tipoTurnoCor }}
              title={`${t.profissionalNome} — ${t.tipoTurnoNome} (${t.horaInicio}–${t.horaFim})${
                est ? ` · ${est.rotulo}` : ""
              }${t.acionado ? " · sobreaviso acionado" : ""}`}
            >
              <span
                className={`inline-flex items-center rounded-full px-1 py-0 text-[9px] font-bold ring-1 ring-inset ${per.classe}`}
              >
                {per.label}
              </span>
              {est && (
                <span
                  className={`inline-flex items-center rounded-full px-1 py-0 text-[9px] font-bold ring-1 ring-inset ${est.classe}`}
                >
                  {est.sigla}
                </span>
              )}
              {t.acionado && (
                <span className="inline-flex items-center rounded-full bg-violet-100 px-1 py-0 text-[9px] font-bold text-violet-800 ring-1 ring-inset ring-violet-200">
                  A
                </span>
              )}
              <span className={`truncate ${risco ? "line-through" : ""}`}>{t.profissionalNome}</span>
            </span>
          );
        })}
        {turnos.length === 0 && (
          <span className="text-[10px] italic text-slate-300">vazio</span>
        )}
      </button>
      {iso === new Date().toISOString().slice(0, 10) && (
        <span className="absolute right-1.5 top-1.5 hidden h-1.5 w-1.5 rounded-full bg-brand-500 group-hover:hidden" />
      )}
    </div>
  );
}

function IconBtn({
  icon: Icon,
  title,
  onClick,
  variant,
}: {
  icon: typeof Copy;
  title: string;
  onClick: () => void;
  variant?: "danger";
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      className={`rounded-sm p-0.5 transition ${
        variant === "danger"
          ? "text-slate-400 hover:bg-red-50 hover:text-red-600"
          : "text-slate-400 hover:bg-brand-50 hover:text-brand-600"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
